import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { MrfiktivHttpClientProvider } from "@/services/mrfiktiv/mrfiktiv-http-client.provider";
import { ThgHttpClientProvider } from "@/services/thg/thg-http-client.provider";
import { CallSessionService } from "../mrfiktiv/v1/CallSessionService";
import {
  MrfiktivRealtimeSessionControllerFindAllParamsGen,
  MrfiktivRealtimeSessionControllerGetStatisticsParamsGen,
  MrfiktivRealtimeSessionControllerGetUsageParamsGen,
  MrfiktivRealtimeSessionViewModelGen,
  MrfiktivExceptionViewmodelGen
} from "../mrfiktiv/v1/data-contracts";
import { ConfigModule } from "@/store/modules/config";

/**
 * Communicate with Call Session API, switching based on app context.
 */
export class CallService {
  mrfiktivProxy: CallSessionService;
  thgProxy: CallSessionService;

  /**
   * Construct the service.
   */
  constructor(mrfiktivHttpClientProvider: MrfiktivHttpClientProvider, thgHttpClientProvider: ThgHttpClientProvider) {
    this.mrfiktivProxy = new CallSessionService(mrfiktivHttpClientProvider.client());
    this.thgProxy = new CallSessionService(thgHttpClientProvider.client());
  }

  /**
   * Returns the proxy for the current app context.
   */
  get proxy(): CallSessionService {
    if (ConfigModule.appContext === AppContextEnum.THG_PORTAL || ConfigModule.appContext === AppContextEnum.THG) {
      return this.thgProxy;
    }

    return this.mrfiktivProxy;
  }

  /**
   * Get all call sessions for a partner.
   */
  async getAll(query: MrfiktivRealtimeSessionControllerFindAllParamsGen) {
    return (await this.proxy.realtimeSessionControllerFindAll(query)).data;
  }

  /**
   * Get one call session by id.
   */
  async getOne(partnerId: string, sessionId: string) {
    return (await this.proxy.realtimeSessionControllerFindOne(partnerId, sessionId)).data;
  }

  /**
   * Get voice-AI-call usage totals for a partner.
   */
  async getUsage(query: MrfiktivRealtimeSessionControllerGetUsageParamsGen) {
    return (await this.proxy.realtimeSessionControllerGetUsage(query)).data;
  }

  /**
   * Get voice-AI-call KPIs / statistics for a partner.
   */
  async getStatistics(query: MrfiktivRealtimeSessionControllerGetStatisticsParamsGen) {
    return (await this.proxy.realtimeSessionControllerGetStatistics(query)).data;
  }

  /**
   * Set the assignees of a call.
   */
  async setAssignees(partnerId: string, sessionId: string, assignees: string[]) {
    return (await this.proxy.realtimeSessionControllerSetAssignees(partnerId, sessionId, { assignees })).data;
  }

  /**
   * Mark multiple calls as read.
   */
  async markManyRead(partnerId: string, ids: string[]) {
    return (await this.proxy.realtimeSessionControllerMarkManyRead(partnerId, { ids })).data;
  }

  /**
   * Close / archive a call.
   */
  async close(partnerId: string, sessionId: string) {
    return (await this.proxy.realtimeSessionControllerClose(partnerId, sessionId)).data;
  }

  /**
   * Reopen a previously closed call.
   */
  async reopen(partnerId: string, sessionId: string) {
    return (await this.proxy.realtimeSessionControllerReopen(partnerId, sessionId)).data;
  }

  /**
   * Mark a single call as unread (clears `viewed`).
   *
   * NOTE: there is no generated proxy method for this — the backend endpoint
   * PATCH /partner/{partnerId}/call/{sessionId}/unread is not implemented yet.
   * This issues the request directly via the proxy's http client so the
   * frontend is ready once the backend ships the endpoint. Until then it 404s.
   */
  async markUnread(partnerId: string, sessionId: string) {
    return (
      await this.proxy.http.request<MrfiktivRealtimeSessionViewModelGen, MrfiktivExceptionViewmodelGen>({
        path: `/partner/${partnerId}/call/${sessionId}/unread`,
        method: "PATCH",
        secure: true,
        format: "json"
      })
    ).data;
  }
}

/**
 * Communicate with Call Session API, switching based on app context.
 */
export default new CallService(new MrfiktivHttpClientProvider(), new ThgHttpClientProvider());
